import type { QueueSnapshot } from '@/api/schemas';
import { Dot } from '@/components/badges';
import { ProviderBadge } from '@/components/ProviderBadge';
import type { StreamStatus } from '@/features/live/useLiveStream';
import { laneState, liveText, liveTone } from './buildsPageModel';

/** Title, live dot and the provider slots of the builds page. */
export function BuildsPageHeader({ projectName, live, lanes }: { projectName: string; live: StreamStatus; lanes: QueueSnapshot['lanes'] }) {
  return (
    <header className="enter flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Builds</h1>
          <LiveBadge live={live} />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Every build of {projectName}, updated live.</p>
      </div>
      {/* Build slots from /queue (design 02 header strip): slots in use per account; the provider badge shows to operators only. */}
      {lanes.length > 0 ? <LaneList lanes={lanes} /> : null}
    </header>
  );
}

function LiveBadge({ live }: { live: StreamStatus }) {
  return (
    <span data-testid="builds-live" data-status={live} className="flex items-center gap-1.5 rounded-md border bg-secondary px-2 py-0.5 font-mono text-[11px] text-subtle" title={`Live updates: ${live}`}>
      {/* Still on purpose: only a build that waits pulses (components/statusMotion.ts). */}
      <Dot tone={liveTone(live)} />
      {liveText(live)}
    </span>
  );
}

function LaneList({ lanes }: { lanes: QueueSnapshot['lanes'] }) {
  return (
    <ul className="flex flex-wrap items-center gap-2" aria-label="Build slots in use">
      {lanes.map((lane) => (
        <LaneItem key={lane.providerAccount.id} lane={lane} />
      ))}
    </ul>
  );
}

function LaneItem({ lane }: { lane: QueueSnapshot['lanes'][number] }) {
  const { busy, label } = laneState(lane);
  return (
    <li data-testid={`queue-lane-${lane.providerAccount.kind}`} title="Build slots in use at this build service" className="flex items-center gap-2 rounded-lg border bg-card px-3 py-1.5 text-xs">
      <ProviderBadge kind={lane.providerAccount.kind} />
      <span className="tabular font-mono text-foreground">
        {lane.inUse}/{lane.concurrencyLimit}
      </span>
      <span className={busy ? 'text-awaiting' : 'text-succeeded'}>{label}</span>
      {lane.waiting.length > 0 ? <span className="text-subtle">· {lane.waiting.length} waiting</span> : null}
    </li>
  );
}
